import { add, nonNans } from "@/_core/data/Advanced";
import { FilterMatchMode, FilterOperator } from "primevue/api/";
import { sortByKey } from "@/_core/_helpers/_helpers";
import { timeDay, timeMonth, timeParse, timeMonday, timeYear, sum } from "d3";

export class AnalyticsHelpers {
  constructor(processData, rawData, userSelections) {
    this.processData = processData;
    this.rawData = rawData;
    this.userSelections = userSelections;
  }

  hedgeConditionNew(d) {
    return (
      d === "hedge" ||
      d === "limited_hedge" ||
      d === "hedge_and_reduce_cost" ||
      d === "ko_hedge" // ||
      // d === "offset_a_long_position" ||
      // d === "offset_a_short_position" ||
      // d === "offset a long position" ||
      // d === "offset a short position"
    );
  }

  monthDiff(d1, d2) {
    // Months between two dates
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months += d2.getMonth() - d1.getMonth();
    return months <= 0 ? 0 : months;
  }

  quarterDate(date) {
    // Return the quarter that the variable "date" belongs to, for nesting
    let tod = new Date();
    let mDiff = this.monthDiff(tod, date);
    let newDate;
    if (mDiff === 0) {
      newDate = timeMonth(date);
    } else {
      let quarters = Math.floor(this.monthDiff(tod, date) / 3);
      newDate = timeDay(
        timeDay(tod.setMonth(tod.getMonth() + 3 * quarters + 1)).setDate(1),
      );
    }
    return newDate;
  }

  timeComparison(date, intervalGroup, data, fieldDate = "date") {
    // Nesting function, returns the appropriate date for nesting
    let dateCopy;
    let durfn;
    if (intervalGroup === "1D") {
      durfn = timeDay(date);
    } else if (intervalGroup === "1W") {
      durfn = timeMonday(date);
    } else if (intervalGroup === "2W") {
      dateCopy = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date))),
      );
      if (date.getDate() < 15) {
        durfn = timeDay(dateCopy.setDate(1));
      } else {
        durfn = timeDay(dateCopy.setDate(15));
      }
    } else if (intervalGroup === "1M") {
      durfn = timeMonth(date);
    } else if (intervalGroup === "1Q") {
      durfn = this.quarterDate(date);
    } else if (intervalGroup === "6M") {
      dateCopy = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(JSON.parse(JSON.stringify(date))),
      );
      if (dateCopy.getMonth() < 6) {
        durfn = timeDay(timeDay(dateCopy.setMonth(0)).setDate(1));
      } else {
        durfn = timeDay(timeDay(dateCopy.setMonth(6)).setDate(1));
      }
    } else if (intervalGroup === "1Y") {
      if (date.getFullYear() === data[0][fieldDate].getFullYear()) {
        durfn = timeDay(
          timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
            JSON.parse(JSON.stringify(data[0][fieldDate])),
          ),
        );
      } else {
        durfn = timeYear(date);
      }
    } else if (intervalGroup === "all") {
      durfn = timeDay(
        timeParse("%Y-%m-%dT%H:%M:%S.%LZ")(
          JSON.parse(JSON.stringify(data[0][fieldDate])),
        ),
      );
    }
    return durfn;
  }

  extendWeekData({ data, initialDate, finalDate, fieldDate = "date" }) {
    if (initialDate == null || finalDate == null) {
      initialDate = this["rawData"]["zoomedData"][0][fieldDate];
      finalDate = this["rawData"]["zoomedData"].at(-1)[fieldDate];
    }
    const dateList = this.processData.createWeekByWeekDays(
      initialDate,
      finalDate,
    );

    const rawDataExtended = [];
    dateList.forEach((d) =>
      rawDataExtended.push({
        [fieldDate]: d,
        buy_sell: "",
        derivative: "",
        derivative_name: "",
        strike: NaN,
        notional: NaN,
        unit: "",
        curr: "",
        data: [],
      }),
    );
    data = data.concat(rawDataExtended);

    const sortedData = sortByKey(data, fieldDate);
    return sortedData;
  }

  /** Sum analysis */
  sumAnalysis({
    data,
    analysisType,
    valueAnalysis = "notional",
    nameAnalysis = "derivative_name",
    multiplyField,
    isMultiplyField = null,
    derivative2 = "",
    isAbs = true,
  }) {
    const sumAnalysis = sum(data, function(d) {
      let valueForAnalysis = d[valueAnalysis] === 0 ? 1 : d[valueAnalysis];
      if (isMultiplyField) {
        valueForAnalysis = d[multiplyField] * valueForAnalysis;
      }
      return d[nameAnalysis] &&
        (d[nameAnalysis] === analysisType || d[nameAnalysis] === derivative2)
        ? Number.isNaN(valueForAnalysis)
          ? 0
          : valueForAnalysis
        : 0;
    });
    return isAbs ? Math.abs(sumAnalysis) : sumAnalysis;
  }

  /** Sum analysis by key */
  sumAnalysisByKey({
    data,
    keyAnalysis = "notional",
    multiplyField = null,
    isAbs = true,
  }) {
    const sumAnalysis = sum(data, function(d) {
      if (multiplyField) {
        return d[multiplyField] * d[keyAnalysis];
      }
      return d[keyAnalysis] ?? 0;
    });
    return isAbs ? Math.abs(sumAnalysis) : sumAnalysis;
  }

  /** Average analysis */
  averageAnalysis({
    data,
    analysisType,
    multiplyField,
    value1Analysis = "notional",
    nameAnalysis = "derivative_name",
    derivative2 = "",
    isMultiplyField = null,
    value2Analysis = value1Analysis,
    calculateDelta = false,
    isAbs = true,
  }) {
    const value1 = this.sumAnalysis({
      data,
      analysisType,
      valueAnalysis: value1Analysis,
      nameAnalysis,
      derivative2,
      multiplyField,
      isMultiplyField,
      isAbs,
    });
    const value2 = this.sumAnalysis({
      data,
      analysisType,
      valueAnalysis: value2Analysis,
      nameAnalysis,
      derivative2,
      isAbs,
    });
    const average = value1 / value2 || 0;
    return calculateDelta ? [value2, average] : average;
  }

  /** Calculates the sum of the values a specific key */
  calculateSum(data, key) {
    return data.map((item) => nonNans(item[key])).reduce(add, 0);
  }

  /** Find unique quantity or price keys */
  findUniqueKeys(data, keyIncludes = []) {
    const uniqueKeys = [];

    data.forEach((item) => {
      const itemKeys = Object.keys(item);
      itemKeys.forEach((key) => {
        let isPrices = key.startsWith("p_");
        const isPriceKey = key.startsWith(isPrices ? "p_" : "q_");

        if (
          (!uniqueKeys.includes(key) && isPriceKey && !key.endsWith("Perc")) ||
          keyIncludes.find((k) => key.startsWith(k.name))
        ) {
          uniqueKeys.push(key);
        }
      });
    });

    return uniqueKeys;
  }

  /** Calculate the total prices for specified data keys */
  getTotalPrices(data, dataKeys) {
    const resultPrices = {};

    dataKeys.forEach((key) => {
      const total = this.calculateSum(data, key);
      const count = data.filter((item) => !Number.isNaN(item[key])).length;
      resultPrices[key] = count > 0 ? total / count : 0;
    });
    return resultPrices;
  }

  // Calculate the sums and their percentages for specified data keys
  getAveragesQty(data, dataKeys) {
    let sumTotals = 0;
    const resultQty = {};

    dataKeys.forEach((key) => {
      resultQty[key] = this.calculateSum(data, key);
      sumTotals += resultQty[key];
    });
    for (const key in resultQty) {
      resultQty[`${key}Perc`] = resultQty[key] / sumTotals;
    }
    return resultQty;
  }

  includesNoNull(str, subStr) {
    return str != null ? str.toLowerCase().includes(subStr) : false;
  }

  isFixationDerivative(derivative) {
    if (derivative == null) {
      return false;
    }
    return (
      derivative.toLowerCase() === "forward" ||
      derivative.toLowerCase() === "future" ||
      derivative.toLowerCase() === "forwards" ||
      derivative.toLowerCase() === "futures"
    );
  }

  selectionStrFct(selVar, strField, defaultField) {
    let txt = "";
    if (selVar.length === 0) {
      txt = "";
    } else if (selVar.length > 1) {
      txt = selVar.length + defaultField;
    } else {
      selVar.forEach(function(d) {
        txt += strField == null ? d : d[strField];
      });
    }
    return txt;
  }

  /** Gets all years available in nested data and idx of those years */
  getYears() {
    if (this.rawData["nestedData"].length === 0) {
      return;
    }
    let yrs = [],
      yr,
      sw = false;
    Object.assign(this.rawData, { years: {} });
    this.rawData["nestedData"].forEach((d, i) => {
      yr = d.date.getFullYear();
      if (!yrs.includes(yr)) {
        yrs.push(yr);
        this.rawData["years"][yr] = { initial: i };
        if (sw) {
          const sortYears = [...yrs].sort();
          const difYears = sortYears.at(-1) - sortYears.at(0);
          const existYear = this.rawData["years"][yr - 1] ? 1 : difYears;
          this.rawData["years"][yr - existYear]["final"] = i;
        }
        sw = true;
      }
    });

    this.rawData["years"][yr]["final"] = this.rawData["nestedData"].length;
    this.rawData["years"]["range"] = [yrs[0], yrs[yrs.length - 1]];
    let year_selected = new Date().getFullYear();

    if (yrs.includes(year_selected)) {
      Object.assign(this.userSelections, { year: year_selected });
    } else {
      Object.assign(this.userSelections, { year: Math.max(...yrs) });
    }

    let xRange = [
      this.rawData["years"][this.userSelections["year"]]["initial"],
      this.rawData["years"][this.userSelections["year"]]["final"],
    ];
    Object.assign(this.userSelections, { xZoom: xRange });
  }

  /** Function to determine what type of derivatives exist */
  existDerivatives(d) {
    if (d.buy_sell === "long" || d.buy_sell === "offset short") {
      if (d.derivative === "forward" || d.derivative === "future") {
        Object.assign(this.rawData, { existLFwd: true });
      } else if (d.derivative === "call") {
        Object.assign(this.rawData, { existLCall: true });
      } else if (d.derivative === "put") {
        Object.assign(this.rawData, { existLPut: true });
      }
    } else if (d.buy_sell === "short" || d.buy_sell === "offset long") {
      if (d.derivative === "forward" || d.derivative === "future") {
        Object.assign(this.rawData, { existSFwd: true });
      } else if (d.derivative === "call") {
        Object.assign(this.rawData, { existSCall: true });
      } else if (d.derivative === "put") {
        Object.assign(this.rawData, { existSPut: true });
      }
    }
  }

  calculateHedgeNotional(d, isHedgeNew = true) {
    if (d.derivative_type.indexOf("OUT") > -1 && d.new_event) {
      return 0;
    } else if (d.derivative_type.indexOf("IN") > -1 && !d.new_event) {
      return 0;
    } else {
      if (d.buy_sell.indexOf("offset") > -1) {
        return 0;
      } else {
        return isHedgeNew ? +d.notional * d.undelying_position : 0;
      }
    }
  }

  addSelection(data, key, field, textParts) {
    if (data && data["value"] != null) {
      textParts.push(" | " + this.selectionStrFct(data["value"], field, key));
    }
  }

  weiAve(p, q, p2, qTot) {
    return (p * q + p2 * (qTot - q)) / qTot;
  }

  fillMissingDates(fieldDate, data) {
    let lastDate = null;

    const result = data.map((item) => {
      if (item[fieldDate]) {
        lastDate = item[fieldDate];
        return item;
      } else {
        return {
          ...item,
          [fieldDate]: lastDate,
        };
      }
    });

    return result;
  }

  addDateFromField(data, fieldDate) {
    const result = data.map((item) => {
      const dateKey = item[fieldDate];

      return {
        ...item,
        date: dateKey,
      };
    });

    return result;
  }
}

/**
 * Define empty Analytics Variable
 */
export function defaultAnalytics() {
  return {
    companies: [{ id: 0, company_name: "N.A." }],
    loading: {
      expoFuture: false,
    },
    rawData: {
      budget: [],
      dataFilters: defineFilters(),
      dDowns: defineDDowns(),
      derivatives: [],
      expoFuture: [],
      expoPast: [],
      hedgesFuture: [],
      hedgesPast: [],
      historicalData: [],
      historicalOHLC: {},
      nestedData: [],
      program: [],
      strategies: [],
      toBeFetched: true,
      toBeFetchedHidden: false,
      years: [],
      zoomedData: [],
    },
    underlyingSpecs: [],
    userSelections: {
      assets: [
        {
          product_name: "N.A.",
          fk_idcurrency: "N.A.",
          fk_idunit: "N.A.",
          currency_name: "N.A/N.A",
        },
      ],
      bucket: "MONTHLY",
      expandSidebar: false,
      initStoreCompPortAsset: null,
      portfolioNums: [],
      portfolios: [],
      selectedAnalysis: "scenarios",
      selectedAnalysisParent: "scenario",
      selectedAsset: [
        {
          product_name: "N.A.",
          fk_idcurrency: "N.A.",
          fk_idunit: "N.A.",
          currency_name: "N.A/N.A",
        },
      ],
      selectedCompanies: [{ id: 0, company_name: "N.A." }],
      selectedPortfolios: [],
      selectedTable: "strategies",
      selectedUnderlying: [{ commodity_name: "N.A." }],
      underlying: [{ commodity_name: "N.A." }],
      thenCandleData: null,
      visibleLeft: false,
      xZoom: [],
    },
    valuation: {
      data: defineEmptyValTable(),
      dDowns: defineDDowns(),
      finalDate: null,
      finalSpot: null,
      initialDate: null,
      initialSpot: null,
      selectedAsset: [],
      valuated: false,
    },
  };
}

/**
 * Define dropdowns for all Analytics
 */
export function defineDDowns() {
  return {
    businessDDown: [],
    clientDDown: [],
    commoditiesDDown: [],
    contractDDown: [],
    counterPDDown: [],
    countryDDown: [],
    expiredDDown: [],
    expoCodeDDown: [],
    expoTypeDDown: [],
    fixStatusDDown: [],
    futuresDDown: [],
    originDDown: [],
    plantDDown: [],
    portfoliosDDown: [],
    pricingMethodDDown: [],
    strategiesDDown: [],
    supplierDDown: [],
  };
}

/**
 * Define filters for all Analytics
 */
export function defineFilters() {
  return {
    base: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    bonus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    buy_sell: { value: null, matchMode: FilterMatchMode.IN },
    client: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    comments: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    commodity_unit: { value: null, matchMode: FilterMatchMode.IN },
    commodity: { value: null, matchMode: FilterMatchMode.IN },
    continuation_month: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    contract_month_year: { value: null, matchMode: FilterMatchMode.IN },
    counterparty: { value: null, matchMode: FilterMatchMode.IN },
    country_of_destination: { value: null, matchMode: FilterMatchMode.IN },
    custom_id: { value: null, matchMode: FilterMatchMode.IN },
    custom_name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    derivative: { value: null, matchMode: FilterMatchMode.IN },
    end_product: { value: null, matchMode: FilterMatchMode.IN },
    eta: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    expired: { value: null, matchMode: FilterMatchMode.IN },
    exposure_type: { value: null, matchMode: FilterMatchMode.IN },
    fixation_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    fixation_status: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_business: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_client: { value: null, matchMode: FilterMatchMode.IN },
    country_of_origin: { value: null, matchMode: FilterMatchMode.IN },
    end_product_str: { value: null, matchMode: FilterMatchMode.IN },
    plant_str: { value: null, matchMode: FilterMatchMode.IN },
    fk_id_provider: { value: null, matchMode: FilterMatchMode.IN },
    fk_idincoterms: { value: null, matchMode: FilterMatchMode.IN },
    freight: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    futures: { value: null, matchMode: FilterMatchMode.IN },
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    hedge_asset: { value: null, matchMode: FilterMatchMode.IN },
    insurance: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    percent_bonus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    plant: { value: null, matchMode: FilterMatchMode.IN },
    portfolio: { value: null, matchMode: FilterMatchMode.IN },
    pricing_method: { value: null, matchMode: FilterMatchMode.IN },
    provider: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    purchase_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    quantity: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    quotation: { value: null, matchMode: FilterMatchMode.IN },
    ratio: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    sale_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    settlement_pnl: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    settlement_price: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    shipping_date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    strike: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    tariffs: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
  };
}

/**
 * Define empty totals for all Analytics
 */
export function defineTotals() {
  return {
    date: "...",
    budgetQ: 0,
    forecastQ: 0,
    ratioQ: 0,
    fixedQ: 0,
    hedgedQ: 0,
    budgetQNorm: 0,
    forecastQNorm: 0,
    ratioQNorm: 0,
    fixedQNorm: 0,
    hedgedQNorm: 0,
    ratioQPerc: 0,
    fixedQPerc: 0,
    hedgedQPerc: 0,
    budgetP: 0,
    fixedP: 0,
    marketP: 0,
    netP: 0,
    fixedPPerc: 0,
    marketPPerc: 0,
    netPPerc: 0,
    budgetPOther: 0,
    fixedPOther: 0,
    marketPOther: 0,
    netPOther: 0,
    fixedPOtherPerc: 0,
    marketPOtherPerc: 0,
    netPOtherPerc: 0,
    budgetPRatio: 0,
    fixedPRatio: 0,
    marketPRatio: 0,
    netPRatio: 0,
    fixedPRatioPerc: 0,
    marketPRatioPerc: 0,
    netPRatioPerc: 0,
    budgetPFut: 0,
    fixedPFut: 0,
    marketPFut: 0,
    netPFut: 0,
    fixedPFutPerc: 0,
    marketPFutPerc: 0,
    netPFutPerc: 0,
    budgetPFutRatio: 0,
    fixedPFutRatio: 0,
    marketPFutRatio: 0,
    netPFutRatio: 0,
    fixedPFutRatioPerc: 0,
    marketPFutRatioPerc: 0,
    netPFutRatioPerc: 0,
  };
}

/**
 * Define empty valuation table for all Analytics
 */
export function defineEmptyValTable() {
  return [
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0,
        },
      ],
      details: [],
      show: false,
      name: "Expired hedges",
      selected: [],
    },
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0,
        },
      ],
      details: [],
      show: false,
      name: "NEW HEDGES",
      selected: [],
    },
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0,
        },
      ],
      details: [],
      show: false,
      name: "OTHER HEDGES",
      selected: [],
    },
    {
      totals: [
        {
          txt_quantity: "Quantity",
          quantity: 0,
          txt_delta: "Delta",
          delta: 0,
          txt_derivatives: "Derivatives",
          n_derivatives: 0,
          txt_strats: "Strategies",
          n_strats: 0,
          txt_value: "Valuation on MM DD, YYY",
          hedge_value: 0,
          txt_value_initial: "Valuation on MM DD, YYY",
          hedge_value_initial: 0,
        },
      ],
      details: [],
      show: false,
      name: "TOTAL OPEN POSITIONS",
      selected: [],
    },
  ];
}

export function getDefaultAnalyticsPreferences(_this) {
  if (
    Object.keys(_this.$store.state.user_preferences.all.items.analytics_choices)
      .length === 0
  ) {
    let choices = {
      selected_nav: 0,
      color_theme: false,
      selected_company: [],
      selected_portfolio:
        _this.$store.state.companies.all.items[0]?.portfolios[0],
      selected_currencies:
        _this.$store.state.companies.all.items[0]?.portfolios[0].fk_currency,
      selected_commodities:
        _this.$store.state.companies.all.items[0]?.portfolios[0].fk_idcommodity,
    };
    _this.$store.state.user_preferences.all.items.analytics_choices = choices;
    // saveUserPreferences(_this);
    return { choices };
  } else {
    let choices =
      _this.$store.state.user_preferences.all.items.analytics_choices;
    return { choices };
  }
}

export function idxOfDate(dates, searchedDate) {
  let idx;
  if (searchedDate >= dates[dates.length - 1]) {
    idx = dates.length - 1;
  } else {
    idx = dates.findIndex((d) => d >= searchedDate);
  }
  return idx;
}
